import { useState, useEffect, useCallback, useRef } from 'react'
import { useClientAuth } from '../../context/ClientAuthContext'
import { Play, Pause, Download, RefreshCw, Search, Clock } from 'lucide-react'

// Bots delete their own recordings after 7 days (cron installed at provision),
// so anything older simply has no file behind it. The date pickers are clamped
// to that window rather than letting a client pick a range that can only 404.
const RETENTION_DAYS = 7

const isoDay = (d) => d.toISOString().split('T')[0]
const daysAgo = (n) => {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return d
}

const fmtDuration = (s) => {
  const n = parseInt(s) || 0
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`
}

const fmtTime = (t) => {
  if (!t) return '—'
  return new Date(t).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit'
  })
}

const DISPO_LABEL = {
  'transfer-call': 'Transferred',
  'not-interested': 'Not interested',
  'answering-machine': 'Answering machine',
  'no-answer': 'No answer',
  'do-not-call': 'Do not call',
  'age-do-not-qualify': 'Not qualified',
  'call-back': 'Call back'
}

export default function ClientRecordings() {
  const { clientApi } = useClientAuth()

  const [recordings, setRecordings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [from, setFrom] = useState(isoDay(daysAgo(RETENTION_DAYS - 1)))
  const [to, setTo] = useState(isoDay(new Date()))
  const [sipFilter, setSipFilter] = useState('')
  const [dispoFilter, setDispoFilter] = useState('')

  const [playingId, setPlayingId] = useState(null)
  const [audioUrl, setAudioUrl] = useState(null)
  const [loadingId, setLoadingId] = useState(null)
  const audioRef = useRef(null)
  // Object URLs are revoked when replaced/unmounted — without this every play
  // leaks a blob for the life of the tab.
  const audioUrlRef = useRef(null)

  const setAudio = useCallback((url) => {
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current)
    audioUrlRef.current = url
    setAudioUrl(url)
  }, [])

  useEffect(() => () => {
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current)
  }, [])

  const fetchRecordings = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const qs = new URLSearchParams({ from, to, limit: '200' })
      if (sipFilter.trim()) qs.set('sip_user', sipFilter.trim())
      if (dispoFilter) qs.set('disposition', dispoFilter)
      const data = await clientApi(`/recordings?${qs.toString()}`)
      if (data.success) setRecordings(data.recordings || [])
      else setError(data.error || 'Failed to load recordings')
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [clientApi, from, to, sipFilter, dispoFilter])

  useEffect(() => { fetchRecordings() }, [fetchRecordings])

  const play = async (rec) => {
    if (playingId === rec.id) {
      // Toggle the already-loaded clip instead of re-downloading it.
      const el = audioRef.current
      if (el) el.paused ? el.play() : el.pause()
      return
    }
    setLoadingId(rec.id)
    setError(null)
    try {
      const blob = await clientApi(`/recordings/${rec.id}/audio`, {}, true)
      setAudio(URL.createObjectURL(blob))
      setPlayingId(rec.id)
      setTimeout(() => audioRef.current?.play().catch(() => {}), 50)
    } catch (e) {
      setError(`Could not load that recording — ${e.message}`)
    } finally {
      setLoadingId(null)
    }
  }

  const download = async (rec) => {
    setLoadingId(rec.id)
    try {
      const blob = await clientApi(`/recordings/${rec.id}/audio`, {}, true)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${rec.sip_user}_${rec.phone_number || 'call'}_${rec.id}.wav`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(`Download failed — ${e.message}`)
    } finally {
      setLoadingId(null)
    }
  }

  const minDate = isoDay(daysAgo(RETENTION_DAYS - 1))
  const maxDate = isoDay(new Date())

  return (
    <div className="client-recordings">
      <div className="client-page-header">
        <h1>Call Recordings</h1>
        <p>{recordings.length} recording{recordings.length === 1 ? '' : 's'} · kept for {RETENTION_DAYS} days</p>
      </div>

      <div style={{
        display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end',
        margin: '16px 0', padding: 16, background: 'var(--card-bg, #fff)',
        border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 10
      }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, opacity: 0.7, marginBottom: 4 }}>From</label>
          <input type="date" value={from} min={minDate} max={maxDate}
                 onChange={e => setFrom(e.target.value)} className="form-input" />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, opacity: 0.7, marginBottom: 4 }}>To</label>
          <input type="date" value={to} min={minDate} max={maxDate}
                 onChange={e => setTo(e.target.value)} className="form-input" />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Bot</label>
          <input type="text" value={sipFilter} placeholder="e.g. 7001"
                 onChange={e => setSipFilter(e.target.value)} className="form-input" style={{ width: 110 }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Outcome</label>
          <select value={dispoFilter} onChange={e => setDispoFilter(e.target.value)} className="form-input">
            <option value="">All</option>
            {Object.entries(DISPO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <button onClick={fetchRecordings} className="btn btn-secondary" disabled={loading}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={15} className={loading ? 'spin' : ''} /> Refresh
        </button>
      </div>

      {error && (
        <div style={{
          padding: 12, marginBottom: 12, borderRadius: 8,
          background: 'rgba(220,38,38,.08)', color: '#b91c1c', fontSize: 14
        }}>{error}</div>
      )}

      {audioUrl && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 5, marginBottom: 12, padding: 12,
          background: 'var(--card-bg, #fff)', border: '1px solid var(--border-color, #e5e7eb)',
          borderRadius: 10
        }}>
          <audio ref={audioRef} src={audioUrl} controls style={{ width: '100%' }} />
        </div>
      )}

      {loading ? (
        <div className="client-loading"><div className="loading-spinner"></div></div>
      ) : recordings.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', opacity: 0.6 }}>
          <Search size={28} style={{ marginBottom: 8 }} />
          <p>No recordings for this range.</p>
          <p style={{ fontSize: 13 }}>Recordings are kept for {RETENTION_DAYS} days.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Time</th>
                <th>Bot</th>
                <th>Number</th>
                <th><Clock size={13} /></th>
                <th>Outcome</th>
                <th>Avatar</th>
                <th style={{ width: 110 }}></th>
              </tr>
            </thead>
            <tbody>
              {recordings.map(r => (
                <tr key={r.id} style={playingId === r.id ? { background: 'rgba(59,130,246,.08)' } : undefined}>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtTime(r.started_at)}</td>
                  <td>{r.sip_user}</td>
                  <td>{r.phone_number || '—'}</td>
                  <td>{fmtDuration(r.duration_seconds)}</td>
                  <td>{DISPO_LABEL[r.disposition] || r.disposition || '—'}</td>
                  <td style={{ opacity: 0.75 }}>{r.avatar_name || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => play(r)} disabled={loadingId === r.id}
                              title="Play" className="btn btn-sm">
                        {loadingId === r.id
                          ? <RefreshCw size={14} className="spin" />
                          : playingId === r.id ? <Pause size={14} /> : <Play size={14} />}
                      </button>
                      <button onClick={() => download(r)} disabled={loadingId === r.id}
                              title="Download" className="btn btn-sm">
                        <Download size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
